import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { ArrowLeft, Save } from 'lucide-react';

export default function EditProduct() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [form,    setForm]    = useState({ title: '', description: '', price: '', quantity: '' });
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [errors,  setErrors]  = useState({});
  const [product, setProduct] = useState(null);

  useEffect(() => {
    api.get(`/products/${id}`)
      .then(({ data }) => {
        const p = data.product;
        setProduct(p);
        setForm({
          title:       p.title,
          description: p.description,
          price:       String(p.price),
          quantity:    String(p.quantity),
        });
      })
      .catch(() => toast.error('Product not found.'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    if (errors[name]) setErrors(er => ({ ...er, [name]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.title.trim())  e.title = 'Title is required.';
    if (!form.description.trim()) e.description = 'Description is required.';
    if (!form.price || Number(form.price) <= 0) e.price = 'Enter a valid price.';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSaving(true);
    try {
      await api.put(`/products/${id}`, {
        title:       form.title,
        description: form.description,
        price:       Number(form.price),
        quantity:    Number(form.quantity),
      });
      toast.success('Listing updated!');
      navigate('/app/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading-center"><div className="spinner spinner-lg" /></div>;

  return (
    <div className="sell-page fade-in">
      <div className="sell-header">
        <button className="btn btn-ghost btn-icon" onClick={() => navigate(-1)}>
          <ArrowLeft size={20} />
        </button>
        <div>
          <h2>Edit Listing</h2>
          <p className="text-muted" style={{ fontSize: '0.875rem' }}>
            Update your listing details
          </p>
        </div>
      </div>

      {/* Cover image preview */}
      {product?.images?.[0] && (
        <div style={{ marginBottom: 'var(--space-6)' }}>
          <img
            src={product.images[0]}
            alt={product.title}
            style={{
              width: 120, height: 120,
              objectFit: 'cover',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--border)',
            }}
          />
          <p className="helper-text" style={{ marginTop: 6 }}>
            Images cannot be changed after listing. Create a new listing if needed.
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ maxWidth: 540 }} noValidate>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>

          <div className="form-group">
            <label className="label">Title</label>
            <input
              className={`input ${errors.title ? 'input-error' : ''}`}
              name="title"
              value={form.title}
              onChange={handleChange}
              maxLength={100}
            />
            {errors.title && <span className="error-text">{errors.title}</span>}
          </div>

          <div className="form-group">
            <label className="label">Description</label>
            <textarea
              className={`textarea ${errors.description ? 'input-error' : ''}`}
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={4}
              maxLength={1000}
            />
            {errors.description && <span className="error-text">{errors.description}</span>}
          </div>

          <div className="sell-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label className="label">Price (₹)</label>
              <input
                className={`input ${errors.price ? 'input-error' : ''}`}
                name="price"
                type="number"
                min="0"
                value={form.price}
                onChange={handleChange}
              />
              {errors.price && <span className="error-text">{errors.price}</span>}
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <label className="label">Quantity</label>
              <input
                className="input"
                name="quantity"
                type="number"
                min="1"
                value={form.quantity}
                onChange={handleChange}
              />
            </div>
          </div>

          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving
              ? <><div className="spinner" /> Saving...</>
              : <><Save size={16} /> Save Changes</>
            }
          </button>
        </div>
      </form>
    </div>
  );
}